import { supabase } from '../lib/supabase'
import { REGULATORY_FRAMEWORK_OPTIONS } from '../data/industries'

// Map framework value keys (e.g., 'hipaa') to human-readable labels (e.g., 'HIPAA')
const FRAMEWORK_LABEL_MAP = Object.fromEntries(
  REGULATORY_FRAMEWORK_OPTIONS.map(f => [f.value, f.label])
)

/**
 * Generate a structured policy template (Tier 1 — Global Templates).
 * Returns structured_content JSON instead of Markdown.
 */
export async function generateStructuredPolicy({ domain, frameworkTags, industryTags }) {
  // Fetch admin's policy style guide settings
  let styleGuide = null
  try {
    const { data } = await supabase
      .from('policy_settings')
      .select('*')
      .maybeSingle()

    if (data) {
      styleGuide = {
        style_guide: data.style_guide,
        firm_name: data.firm_name,
        document_control_template: data.document_control_template,
        standard_disclaimer: data.standard_disclaimer,
        default_review_cycle: data.default_review_cycle,
        language_preferences: data.language_preferences,
        preferred_role_titles: data.preferred_role_titles
      }
    }
  } catch {
    // No settings configured
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 180000)

  try {
    const response = await fetch('/api/ai/policy-generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        domain,
        frameworkTags,
        industryTags,
        styleGuide,
        mode: 'structured'
      }),
      signal: controller.signal
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Generation failed' }))
      throw new Error(err.error || 'AI structured policy generation failed')
    }

    return response.json()
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Generation timed out. Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

export async function generatePolicy({ domain, frameworkTags, industryTags, clientContext }) {
  // Fetch admin's policy style guide settings
  let styleGuide = null
  try {
    const { data } = await supabase
      .from('policy_settings')
      .select('*')
      .maybeSingle()

    if (data) {
      styleGuide = {
        style_guide: data.style_guide,
        firm_name: data.firm_name,
        document_control_template: data.document_control_template,
        standard_disclaimer: data.standard_disclaimer,
        default_review_cycle: data.default_review_cycle,
        language_preferences: data.language_preferences,
        preferred_role_titles: data.preferred_role_titles
      }
    }
  } catch {
    // No settings configured — generation will use fallback defaults
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 180000)

  try {
    const response = await fetch('/api/ai/policy-generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain, frameworkTags, industryTags, clientContext, styleGuide }),
      signal: controller.signal
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Generation failed' }))
      throw new Error(err.error || 'AI policy generation failed')
    }

    return response.json()
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Generation timed out. Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Tier 2: Refine a single section of structured policy content using Sonnet.
 * @param {Object} params
 * @param {string} params.domain - Policy domain name
 * @param {Object} params.section - The section to refine (from structured_content.policy_statements)
 * @param {string[]} params.frameworkTags - Framework tags for context
 * @param {string} params.instruction - Admin's refinement instruction (e.g., "make more specific to healthcare")
 * @returns {Promise<{section: Object}>}
 */
export async function refineSection({ domain, section, frameworkTags, instruction }) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 120000)

  try {
    const response = await fetch('/api/ai/policy-enrich', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: 'section-refine',
        domain,
        section,
        frameworks: frameworkTags || [],
        instruction
      }),
      signal: controller.signal
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Refinement failed' }))
      throw new Error(err.error || 'AI section refinement failed')
    }

    return response.json()
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Refinement timed out. Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Tier 3: Enrich structured policy content with client-specific context and produce gap report.
 * @param {Object} params
 * @param {string} params.domain - Policy domain name
 * @param {Object} params.structuredContent - The structured_content JSON
 * @param {string[]} params.frameworkTags - Framework abbreviations
 * @param {Object} params.clientContext - Client context for tailoring
 * @returns {Promise<{structured_content: Object, gap_report: Array}>}
 */
export async function enrichStructuredPolicy({ domain, structuredContent, frameworkTags, clientContext }) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 180000)

  try {
    const response = await fetch('/api/ai/policy-enrich', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: 'tier3-enrich',
        domain,
        structuredContent,
        frameworks: frameworkTags || [],
        clientContext
      }),
      signal: controller.signal
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Enrichment failed' }))
      throw new Error(err.error || 'AI Tier 3 enrichment failed')
    }

    return response.json()
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Enrichment timed out. Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Enrich base policy content with framework-specific regulatory language.
 * @param {Object} params
 * @param {string} params.content - Base policy Markdown content
 * @param {string} params.domain - Policy domain name
 * @param {string[]} params.frameworks - Framework value keys (e.g., ['hipaa', 'pci-dss'])
 * @param {Object} [params.clientContext] - Optional client context for tailoring
 * @param {Array} [params.consolidatedPolicies] - Child policies consolidated into this parent
 * @returns {Promise<{content: string, enriched: boolean}>}
 */
export async function enrichPolicy({ content, domain, frameworks, clientContext, consolidatedPolicies }) {
  // Convert framework keys to human-readable labels for the AI prompt
  const frameworkLabels = frameworks.map(f => FRAMEWORK_LABEL_MAP[f] || f)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 180000)

  try {
    const response = await fetch('/api/ai/policy-enrich', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, domain, frameworks: frameworkLabels, clientContext, consolidatedPolicies }),
      signal: controller.signal
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Enrichment failed' }))
      throw new Error(err.error || 'AI policy enrichment failed')
    }

    return response.json()
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Enrichment timed out. Policy was copied without enrichment.')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}
